/**
 * 剧本角色卡卡体（2026-10-25 抽公共）：剧头（名/类型/题材）＋简介折叠＋左右两列（角色介绍+周期片酬 / 属性门槛）。
 * 三处共用同一份 markup，⛔ 任何一处再手写内联模板：
 *  - 试镜周框内详情（`MonthProgressPanel.showPoolDetail`，门槛不带达标标记）
 *  - 直签送达链的重档签约弹窗（`app.ts` 送达链 ①，走 `ContractSignPop` 重档，门槛带达标 dim）
 *  - 人脉页角色项的剧本详情层（`CharactersPanel.directDetail` = `RoleDetailPop`，带达标 dim）
 * 样式＝`.pop-detail`/`pd-` 一族（ui.css，2026-10-25 起脱离 `.month-full` 上下文，三处同形同源）。
 * 差异只有两点，都走参数：`roleTitle`（左列标题整句，各处称呼不同）与门槛行呈现模式：
 *  - 缺省 plain＝普通灰字（未试镜/试镜失败锁已重置可重试——颜色强调的是**试镜结果**，不是属性预判）
 *  - `dimUnmetReq`＝未达标灰化（直签重档/人脉详情：预览型语义，全站既有）
 *  - `reqTone: 'ok'|'no'`＝门槛行红绿（2026-10-28 茄子定：颜色=试镜结果的强调——
 *    通过试镜→整组绿且一直显示（资格在池就一直绿）；本周试镜失败锁着→**逐行判**：
 *    达标行绿、没达到的行红；锁重置可重试→回灰。
 *    消费方按 auditionQualified/isAuditionLocked 现算传入）。三选一，reqTone 优先于 dimUnmetReq。
 */
import { ScriptDef, ScriptRoleDef, AttributeKey } from '../data/types';
import { attrName, createDiv, createButton } from './ui';
import { Utils } from '../core/Utils';
import { GameState } from '../systems/GameState';
import { ScriptSystem } from '../systems/ScriptSystem';

/** 剧本类型中文名（原 MonthProgressPanel 模块私有，随卡体抽公共迁出） */
export function genreName(genre: ScriptDef['genre']): string {
    const map: Record<string, string> = { movie: '电影', tv: '电视剧', variety: '综艺', web: '网剧', short: '短剧', other: '其他' };
    return map[genre] ?? genre;
}

/** 卡体 HTML（inner）：容器由调用方给（`.pop-detail` 或包着它的壳），这里只出内容 */
export function roleCardHtml(
    script: ScriptDef,
    role: ScriptRoleDef,
    opts: { roleTitle: string; dimUnmetReq?: boolean; reqTone?: 'ok' | 'no' },
): string {
    const long = script.synopsis.length > 70;
    const reqs = role.requirements.map(req => {
        const attr = req.attr as AttributeKey;
        if (opts.reqTone) {
            // 红绿=试镜结果强调（2026-10-28 茄子定）：通过→整组绿；失败→**逐行判**——
            // 达标行绿、没达到的行红（缺口一眼可见）；只上色不加字
            const cur = GameState.getInstance().getAttribute(attr);
            const lineOk = cur >= req.value;
            const cls = opts.reqTone === 'ok' || lineOk ? 'req-ok' : 'req-no';
            return `<div class="req-line ${cls}">${attrName(attr)} ≥ ${req.value}</div>`;
        }
        const ok = GameState.getInstance().getAttribute(attr) >= req.value;
        return `<div class="req-line${opts.dimUnmetReq && !ok ? ' dim' : ''}">${attrName(attr)} ≥ ${req.value}</div>`;
    }).join('');
    return `<div class="pd-head"><div class="pd-name">${script.name}</div><div class="pd-sub"><span class="pd-type">${genreName(script.genre)}</span><span class="pd-themes">${script.themes.join('·')}</span></div></div>`
        + `<p class="pd-intro${long ? ' clamped2' : ''}">${script.synopsis}</p>`
        + (long ? `<span class="pd-fold">展开全文 ▾</span>` : '')
        + `<div class="pd-cols">`
        +   `<div class="pd-col l">`
        +     `<div class="col-t">${opts.roleTitle}</div>`
        +     (role.intro ? `<div class="col-body">${role.intro}</div>` : '')
        +     `<div class="pd-cells"><div class="pd-cell"><span class="k">周期</span><span class="v">${role.shootWeeks} 周</span></div><div class="pd-cell"><span class="k">片酬</span><span class="v">¥${Utils.formatMoney(ScriptSystem.getInstance().rewardOf(role))}</span></div></div>`
        +   `</div>`
        +   `<div class="pd-col r">`
        +     `<div class="col-t">属性门槛</div>`
        +     `<div>${reqs}</div>`
        +   `</div>`
        + `</div>`;
}

/** 简介「展开全文 ▾」折叠绑定（替代三处各自的 getElementById 版；scope＝卡体所在的容器） */
export function bindPdFold(scope: ParentNode): void {
    const fold = scope.querySelector<HTMLElement>('.pd-fold');
    const intro = scope.querySelector<HTMLElement>('.pd-intro');
    if (!fold || !intro) return;
    fold.addEventListener('click', e => {
        e.stopPropagation();
        const clamped = intro.classList.toggle('clamped2');
        fold.textContent = clamped ? '展开全文 ▾' : '收起 ▴';
    });
}

/**
 * 角色详情弹层组件（2026-10-25 抽公共）：遮罩背板＋居中卡（`.reveal-detail-overlay`+`.reveal-detail-card`）
 * ＋公共卡体（roleCardHtml）＋空按钮行。**关闭交互全站统一＝点弹窗外/esc，⛔ 返回键**（2026-10-25 茄子令）。
 * 两处消费：5c 竞角揭晓详情（MonthProgressPanel，挂执行页面板内）＋人脉页直签戏约详情
 * （CharactersPanel，挂 app.overlayRoot——弹窗挂壳口径）。差异只在按钮行内容（签约/带资进组 vs 放弃/签约），
 * 由调用方 open() 后 addButton 填。
 */
export class RoleDetailPop {
    readonly overlay: HTMLElement;
    private body: HTMLElement;
    private btnRow: HTMLElement | null = null;

    /** parent＝挂载父（5c=执行页面板根、直签=app.overlayRoot） */
    constructor(parent: HTMLElement) {
        this.overlay = createDiv('dimoverlay reveal-detail-overlay');
        this.body = createDiv('modal-note-card script-detail reveal-detail-card');
        this.overlay.appendChild(this.body);
        parent.appendChild(this.overlay);
        // 背板点击关（点卡内不关）＋点击⛔冒泡（宿主面板有自己的点屏推进）＋esc 同款
        this.overlay.addEventListener('click', e => {
            e.stopPropagation();
            if (e.target === this.overlay) this.close();
        });
        this.overlay.dataset.esc = 'self';
    }

    get isOpen(): boolean {
        return this.overlay.classList.contains('show');
    }

    /** 打开并渲染卡体；返回按钮行容器（调用方直接 append，或用 addButton 便捷加） */
    open(
        script: ScriptDef,
        role: ScriptRoleDef,
        opts: { roleTitle: string; dimUnmetReq?: boolean; reqTone?: 'ok' | 'no' },
    ): HTMLElement {
        this.body.innerHTML = '';
        const detail = createDiv('pop-detail');
        detail.innerHTML = roleCardHtml(script, role, opts);
        this.btnRow = createDiv('btn-row');
        detail.appendChild(this.btnRow);
        this.body.appendChild(detail);
        bindPdFold(detail);
        this.overlay.classList.add('show');
        return this.btnRow;
    }

    /** 按钮行加一颗（pool-cta 族；文案/行为/置灰由调用方定） */
    addButton(cls: string, text: string, onClick?: () => void): HTMLButtonElement {
        const btn = createButton(cls, text, onClick);
        (this.btnRow ?? createDiv('btn-row')).appendChild(btn);
        return btn;
    }

    close(): void {
        this.overlay.classList.remove('show');
    }
}
